import { openProfileDocument } from '../../../backend/transport';
import React, { useEffect, useRef, useState } from 'react';
import { SearchBar, TitleBar, BlpTiles, DropDown } from '../../common';
import {
  BUTTON,
  HEADING,
  PKJ_STATUS,
  POPUP_MSG,
  POPUP_TYPE,
  ROUTES,
  TITLES,
} from '../../../constants';
import { Button } from '../../shared';
import { useNavigate, useSearchParams } from '../../../backend/navigation';
import { StatusBox } from '../manufacturing/clothManufacturing/StatusBox';
import { popup } from '../../../utils';
import { vendorPackage, vendorPackageLots, printLabelPackage } from '../../../service/package';
import { useDebounce } from '../../../hooks';
import { useIntersectionObserver } from '../../../utils/useIntersectionObserver';
import { packageDataCompiler } from '../../../utils/dataCompiler/packageDataCompiler';
import PrintLabelModalBulk from './PrintLabelModalBulk';

export const PackageLotsDetail = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values.
  // The page now lists LOTS (paginated); packages are fetched lazily per lot
  // on expand and cached in lotPackages so re-expanding doesn't refetch.
  const [lots, setLots] = useState([]);
  const [expandedLotIds, setExpandedLotIds] = useState(new Set());
  const [lotPackages, setLotPackages] = useState({}); // lot_id -> compiled package tiles
  const [loadingLotIds, setLoadingLotIds] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false); // Expand All active; new lots (infinite scroll) arrive expanded too
  const sortingArray1 = ['Single Box', 'Mix Box'];
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sort1, setSort1] = useState(searchParams.get('boxType') || 'Box Type');
  const [printMode, setPrintMode] = useState(searchParams.get('printMode') === 'true' || false);
  const [selectedBlp, setSelectedBlp] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1); // last lots page fetched; infinite scroll appends beyond it
  const [hasMoreLots, setHasMoreLots] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const [selectionMode, setSelectionMode] = useState("page"); // "page" | "all"
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [globalSelectAll, setGlobalSelectAll] = useState(false);

  // Update URL params when values change
  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    // Only include filters if they're not the default values
    if (params.boxType && params.boxType !== 'Box Type') newParams.boxType = params.boxType;
    if (params.printMode !== undefined) newParams.printMode = params.printMode.toString();

    setSearchParams(newParams, { replace: true });
  };

  const onTileClick = (to, data) => {
    navigate(to, { state: data });
  };

  // Search/sort predicates shared by the first lots fetch and every infinite-scroll
  // page after it - both must stay in sync or scrolling would silently drop a filter.
  const buildFilterParams = () => ({
    search: searchTerm
      ? {
        name: searchTerm,
        'quant_ids.lot_id.name': searchTerm,
        'quant_ids.product_id.default_code': searchTerm,
      }
      : {},
    sort:
      sort1 === 'Box Type'
        ? {}
        : { selection: { is_mixed: sort1 === 'Single Box' ? false : true } },
  });

  const getLotList = useDebounce((params = {}, { append = false } = {}) => {
    if (append) setIsLoadingMore(true); else setIsLoading(true);
    vendorPackageLots({ data: { page_number: params.page_number || pageNumber, ...params } })
      .then((response) => {
        const records = response?.data?.records || [];
        const pagination = response?.data?.params || {};
        setLots((prev) => (append ? [...prev, ...records] : records));
        setHasMoreLots((pagination.end || 0) < (pagination.total_records || 0));
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  }, 400);

  const loadMoreLots = () => {
    if (isLoadingMore || !hasMoreLots) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    getLotList(
      { page_number: nextPage, ...buildFilterParams() },
      { append: true },
    );
  };

  useIntersectionObserver({
    target: sentinelRef,
    onIntersect: loadMoreLots,
    enabled: hasMoreLots && !isLoadingMore && !isLoading,
  });

  const fetchLotPackages = (lotId) => {
    setLoadingLotIds((prev) => new Set(prev).add(lotId));
    // sort1 (Box Type) is a per-box predicate, not a lot-level one — it must be
    // threaded through here too, or the lot's header count (server-filtered)
    // disagrees with the boxes shown on expand. sort1 comes from the closure at
    // call time; that's safe because getList always clears the cache and
    // collapses lots before it can change, so every fetch happens after the
    // relevant filter state has settled.
    const sort = sort1 === 'Box Type' ? {} : { selection: { is_mixed: sort1 === 'Single Box' ? false : true } };
    return vendorPackage({ data: { lot_id: lotId, page_number: 1, sort } })
      .then((response) => {
        const records = response?.data?.records || [];
        setLotPackages((prev) => ({ ...prev, [lotId]: packageDataCompiler(records) }));
      })
      .finally(() => {
        setLoadingLotIds((prev) => {
          const next = new Set(prev);
          next.delete(lotId);
          return next;
        });
      });
  };

  const toggleLot = (lotId) => {
    setExpandedLotIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) {
        next.delete(lotId); // collapse only; cached packages stay for next expand
      } else {
        next.add(lotId);
        if (!lotPackages[lotId]) fetchLotPackages(lotId);
      }
      return next;
    });
  };

  // Expands the given lots and fetches packages for any not yet cached, 4 at a
  // time so Expand All doesn't fire one request per lot simultaneously.
  // ponytail: fixed-size sequential batches, not a real concurrency pool -
  // fine at the lot counts this page loads; revisit if that grows a lot.
  const expandLots = (lotIds) => {
    setExpandedLotIds((prev) => new Set([...prev, ...lotIds]));
    const uncached = lotIds.filter((id) => !lotPackages[id]);
    const batchSize = 4;
    let chain = Promise.resolve();
    for (let i = 0; i < uncached.length; i += batchSize) {
      const batch = uncached.slice(i, i + batchSize);
      chain = chain.then(() => Promise.all(batch.map(fetchLotPackages)));
    }
    return chain;
  };

  // Setting the flag is enough - the effect below reacts to it and does the
  // actual expand/fetch, so lots arriving later while it's active go through
  // the same single path instead of a second, racing call here.
  const handleExpandAll = () => setAllExpanded(true);

  const handleCollapseAll = () => {
    setAllExpanded(false);
    setExpandedLotIds(new Set());
  };

  // Lots that load later (infinite scroll) while Expand All is active arrive expanded too.
  useEffect(() => {
    if (allExpanded) expandLots(lots.map((lot) => lot.lot_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lots, allExpanded]);

  const getList = useDebounce((additionalParams = {}) => {
    setIsLoading(true);
    setGlobalSelectAll(false);
    setSelectionMode("page");

    // Filters/page changed the lot set — drop cached per-lot packages and
    // collapse everything so a re-expand refetches under the new scope.
    setLotPackages({});
    setExpandedLotIds(new Set());

    const { page_number: _ignoredPage, ...restParams } = additionalParams;

    // Every filter/search change is a reset to page 1 - infinite scroll only
    // ever appends forward from there via loadMoreLots.
    const params = {
      page_number: 1,
      ...buildFilterParams(),
      ...restParams,
    };

    setPageNumber(1);

    // Update URL params
    updateUrlParams({
      search: restParams.search ? (restParams.search.name || '') : searchTerm,
      boxType: restParams.sort ? (restParams.sort.selection ? (restParams.sort.selection.is_mixed ? 'Mix Box' : 'Single Box') : 'Box Type') : sort1,
      printMode: printMode,
    });

    getLotList(params);
  }, 0);

  // Total packages across every lots page loaded so far (infinite scroll appends
  // into `lots`, so this grows as the user scrolls). The lots endpoint doesn't
  // return a package total, so this is a best-effort count for the print-limit
  // guard and the "Select All" label.
  // ponytail: undercounts until the user has scrolled through every lot page;
  // ask backend for a real total_package_count on /pwa/packages/lots if this
  // needs to be exact without scrolling.
  const totalPackagesOnPage = lots.reduce((sum, lot) => sum + (lot.package_count || 0), 0);

  const initiallyLoaded = useRef(false);
  useEffect(() => {
    if (initiallyLoaded.current) return;
    initiallyLoaded.current = true;
    getList();
  }, [getList]);

  const submitSearch = (value) => {
    setSearch(value);
    if (value) getList();
  };
  const changeSearchTerm = (value) => {
    setSearchTerm(value);
    if (!value && search) {
      setSearch(false);
      getList({ search: {} });
    }
  };
  const changeSort = (value) => {
    setSort1(value);
    getList({ sort: value === 'Box Type' ? {} : { selection: { is_mixed: value !== 'Single Box' } } });
  };
  const updatePrintMode = (value) => {
    setPrintMode(value);
    updateUrlParams({ search: searchTerm, boxType: sort1, printMode: value });
    if (!value) {
      setSelectedBlp([]);
      setSelectionMode("page");
      setExcludedIds(new Set());
      setGlobalSelectAll(false);
    }
  };
  const resetFilter = () => changeSort('Box Type');

  const addPackageToList = (id) => {
    if (selectionMode === "all") {
      const next = new Set(excludedIds);
      if (excludedIds.has(id)) {
        next.delete(id); // re-check
      } else {
        next.add(id); // uncheck
      }
      setExcludedIds(next);
    } else {
      if (selectedBlp.includes(id)) {
        // remove id
        setSelectedBlp((prev) => prev.filter((pkgId) => pkgId !== id));
      } else {
        // add id
        setSelectedBlp((prev) => [id, ...prev]);
      }
    }
  };

  const checked = (id) => {
    if (selectionMode === "all") {
      return !excludedIds.has(id);
    }
    return selectedBlp.includes(id);
  };

  const handleSelectAllVisible = () => {
    // "Select Page" now means every package fetched so far in expanded lots
    // (lots not yet expanded haven't loaded packages to select).
    const visiblePackageIds = Array.from(expandedLotIds).flatMap(
      (lotId) => lotPackages[lotId]?.map((pkg) => pkg.id) || [],
    );
    visiblePackageIds.forEach(id => {
      if (!checked(id)) {
        addPackageToList(id);
      }
    });
    setSelectionMode("page");
    setExcludedIds(new Set());
  };

  const handleSelectAllMatching = () => {
    setSelectionMode("all");
    setExcludedIds(new Set());
    setGlobalSelectAll(true);
    setSelectedBlp([]);
  };

  const bulkPrint = () => {
    if (printMode) {
      const totalSelected = selectionMode === "all"
        ? totalPackagesOnPage - excludedIds.size
        : selectedBlp.length;

      if (totalSelected > 100) {
        popup(POPUP_TYPE.ERR, POPUP_MSG.PRINT_LIMIT);
      } else if (totalSelected > 0) {
        setIsModalOpen(true);
      } else {
        popup(POPUP_TYPE.ERR, 'No Package Selected');
      }
    } else {
      updatePrintMode(true);
      popup(POPUP_TYPE.SUCCESS, POPUP_MSG.SELECT_PACKAGE);
    }
  };

  const cancelPrint = () => {
    updatePrintMode(false);
    setSelectedBlp([]);
    setSelectionMode("page");
    setExcludedIds(new Set());
    setGlobalSelectAll(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitModal = (payload) => {
    let finalPayload;

    if (selectionMode === "all") {
      // Global selection payload - similar to BillsContent. Resolved server-side
      // by search/sort + excluded_ids, so it correctly covers packages in lots
      // the user never expanded client-side - that's intended, not a bug.
      const sortSelection = sort1 === 'Box Type'
        ? {}
        : { is_mixed: sort1 === 'Single Box' ? false : true };

      finalPayload = {
        ...payload,
        select_all: true,
        search: searchTerm
          ? {
            name: searchTerm,
            'quant_ids.lot_id.name': searchTerm,
            'quant_ids.product_id.default_code': searchTerm,
          }
          : {},
        sort: {
          selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
        },
        excluded_ids: Array.from(excludedIds),
      };
    } else {
      // Page selection payload - existing logic.
      finalPayload = {
        ...payload,
        select_all: false,
        package_ids: selectedBlp,
      };
    }

    // Make an API call or perform other actions with the payload
    printLabelPackage({
      data: finalPayload,
    })
      .then((response) => {
        if (!response) return;
        if (response.data.length > 0) {
          const url = response.data[0].split('?')[0];
          openProfileDocument(url);
          updatePrintMode(false);
          setSelectedBlp([]);
          setSelectionMode("page");
          setExcludedIds(new Set());
          setGlobalSelectAll(false);
        }
      });
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.PACKAGE, route: ROUTES.PACKAGE }]} />
      <div className="bg-white p-3 package-top-info border-def-up border-def-down">
        <StatusBox status={PKJ_STATUS} />
        <div className="d-flex align-items-center justify-content-end mb-3 top-header-set">
          <div className="d-flex align-items-center">
            <h4 className="me-3 mb-0">{HEADING.BOX_COUNT}</h4>
            <div className="number-count d-flex align-items-center justify-content-center">
              {totalPackagesOnPage}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-start gap-3 w-100">
          <SearchBar
            search={search}
            setSearchTerm={changeSearchTerm}
            searchTerm={searchTerm}
            setSearch={submitSearch}
            onDefaultSearch={getList}
          />
          <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
            <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={changeSort} />
            <Button
              clickHandler={allExpanded ? handleCollapseAll : handleExpandAll}
              className="white-btn d-flex align-items-center justify-content-center px-3 ms-2">
              {allExpanded ? BUTTON.COLLAPSE_ALL : BUTTON.EXPAND_ALL}
            </Button>
            {sortingArray1.includes(sort1) && (
              <Button
                clickHandler={resetFilter}
                className="blue-btn d-flex align-items-center justify-content-center px-3 ms-2">
                {BUTTON.CLEAR_FILTERS}
              </Button>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-between gap-2">
          <div className="d-flex justify-content-start align-items-center gap-2">
            {printMode && (
              <>
                {!globalSelectAll && (
                  <Button
                    clickHandler={handleSelectAllVisible}
                    className="blue-btn d-flex align-items-center justify-content-center px-4 ">
                    Select Page
                  </Button>
                )}

                {/* Selection count and "Select All" link - similar to BillsContent */}
                {(selectedBlp.length > 0 && !globalSelectAll) && (
                  <>
                    <span className="text-muted">
                      {selectedBlp.length} {selectedBlp.length === 1 ? 'record' : 'records'} selected
                    </span>
                    {!(selectedBlp.length === totalPackagesOnPage) && (
                      <span
                        onClick={handleSelectAllMatching}
                        className="text-primary cursor-pointer fw-semibold"
                        role="button"
                      >
                        Select All ({totalPackagesOnPage})
                      </span>
                    )}
                  </>
                )}

                {/* Global selection display - similar to BillsContent */}
                {selectionMode === 'all' && (
                  <>
                    <span className="text-muted">
                      {totalPackagesOnPage - excludedIds.size}{' '}
                      {(totalPackagesOnPage - excludedIds.size) === 1 ? 'record' : 'records'} selected
                    </span>
                    {excludedIds.size > 0 && (
                      <span
                        onClick={handleSelectAllMatching}
                        className="text-primary cursor-pointer fw-semibold"
                        role="button"
                      >
                        Select All ({totalPackagesOnPage})
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {Array.isArray(lots) && lots.length > 0 && (
            <div className="d-flex justify-content-end gap-2">
              <Button
                clickHandler={bulkPrint}
                className="white-btn d-flex align-items-center justify-content-center px-4 ">
                {BUTTON.PRINT_LABEL}
              </Button>
              {printMode && (
                <Button
                  clickHandler={cancelPrint}
                  className="white-btn d-flex align-items-center justify-content-center px-4 ">
                  {BUTTON.CANCEL}
                </Button>
              )}
              {!printMode && (
                <Button
                  clickHandler={() => navigate(ROUTES.ADD_MIX_BOX, { state: Object.values(lotPackages).flat() })}
                  className="white-btn d-flex align-items-center justify-content-center ms-2 px-3 ">
                  {BUTTON.ADD_BOX}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="bg-white p-3 pt-0 package-titles-info pkj-lot-accordion">
        {isLoading ? (
          <div className="text-center my-2">
            <span className="spinner-border spinner-border-sm text-primary" role="status" />
            <span className="ms-2">Loading...</span>
          </div>
        ) : Array.isArray(lots) && lots.length > 0 ? (
          lots.map((lot) => {
            const isExpanded = expandedLotIds.has(lot.lot_id);
            const isLotLoading = loadingLotIds.has(lot.lot_id);
            const tiles = lotPackages[lot.lot_id];
            return (
              <div key={lot.lot_id} className="accordion-item">
                <div
                  className="d-flex justify-content-between align-items-center lot-header cursor-pointer"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleLot(lot.lot_id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleLot(lot.lot_id);
                    }
                  }}
                  data-testid={`lot-header-${lot.lot_id}`}
                >
                  <div>
                    <div className="lot-title">
                      {lot.lot_name || 'No Lot'}
                      <span className="lot-count-pill">{lot.package_count}</span>
                    </div>
                    {lot.skus?.length > 0 && (
                      <div className="lot-skus">SKUs: {lot.skus.join(', ')}</div>
                    )}
                  </div>
                  <span className="lot-chevron">{isExpanded ? '▲' : '▼'}</span>
                </div>
                <div className={`collapse ${isExpanded ? 'show' : ''}`}>
                  <div className="row custom-row p-2 lot-body">
                    {isLotLoading ? (
                      <div className="lot-loading w-100 my-2">Loading boxes…</div>
                    ) : tiles && tiles.length > 0 ? (
                      tiles.map((tile, index) => (
                        <BlpTiles
                          key={index}
                          tile={tile}
                          clickHandler={printMode ? bulkPrint : onTileClick}
                          to={ROUTES.PACKAGE_BLP_OVERVIEW}
                          printMode={printMode}
                          selectedBlp={selectedBlp}
                          setSelectedBlp={setSelectedBlp}
                          addPackageToList={addPackageToList}
                          checked={checked}
                          selectionMode={selectionMode}
                        />
                      ))
                    ) : tiles ? (
                      <div>
                        <h5 className="px-1 text-center py-3">No packages in this lot</h5>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div>
            <h5 className="px-1 text-center py-5">No Records Found</h5>
          </div>
        )}
      </div>
      {Array.isArray(lots) && lots.length > 0 && (
        <>
          <div ref={sentinelRef} />
          {isLoadingMore && (
            <div className="text-center my-2">
              <span className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="ms-2">Loading more lots…</span>
            </div>
          )}
        </>
      )}
      {isModalOpen && (
        <PrintLabelModalBulk
          onClose={handleCloseModal}
          onSubmit={handleSubmitModal}
          packageData={selectedBlp}
        />
      )}
    </>
  );
};